import { roundRect } from "./misc";

export interface WaveformEventType {
  eventType: "COUGH" | "COUGH_1" | "COUGH_2" | "PEAK" | "FRAME";
  startTs: number;
  endTs: number;
}
type FFTSample = {
  sample: number;
  timestamp: number;
};

export interface WaveformCfg {
  sampleRate?: number;
  columWidthPx?: number;
  columnGapPx?: number;
  samplesInColumn?: number;
  canvasWidthPx?: number;
  canvasHeightPx?: number;
}

export class Waveform {
  canvas: HTMLCanvasElement;
  context: CanvasRenderingContext2D;
  events: WaveformEventType[];
  fftData: FFTSample[];
  playing: boolean = false;

  SAMPLE_RATE: number;

  // Pixels each column occupies must be exact Integer because of canvas antialiasing
  COL_SIZE_DATA_PX: number;
  COL_SIZE_GAP_PX: number;
  COL_SIZE_PX: number;

  // Number of samples each column represents, must be power of 2
  SAMPLES_IN_COLUMN: number;

  // Width of the canvas
  CANVAS_WIDTH_PX: number;

  // Dependant parameters:
  // Number of columns show in the waveform
  NUM_COLUMNS: number;

  // How many MS of audio can be shown at once in the canvas
  AUDIO_DURATION_IN_DISPLAY_MS: number;

  // const COL_DURATION_MS:number;
  COL_DURATION_MS: number;

  // Buffer size inside the Analyzer Node
  FFT_SIZE: number;

  _frame_id: number | undefined;

  constructor(canvas: HTMLCanvasElement, cfg: WaveformCfg, autoPlay = false) {
    this.canvas = canvas;
    this.events = [];
    this.fftData = [];

    const {
      sampleRate = 44100,
      columWidthPx = 6,
      columnGapPx = 6,
      samplesInColumn = 2 ** 11,
      canvasWidthPx = 1000,
      canvasHeightPx = 400,
    } = cfg;

    this.SAMPLE_RATE = sampleRate;

    // Pixels each column occupies must be exact Integer because of canvas antialias
    this.COL_SIZE_DATA_PX = columWidthPx;
    this.COL_SIZE_GAP_PX = columnGapPx;
    this.COL_SIZE_PX = this.COL_SIZE_DATA_PX + this.COL_SIZE_GAP_PX;

    // Number of samples each column represents, must be power of 2
    this.SAMPLES_IN_COLUMN = samplesInColumn;

    // Width of the canvas
    this.CANVAS_WIDTH_PX = canvasWidthPx;

    // Dependant parameters:
    // Number of columns show in the waveform
    this.NUM_COLUMNS = Math.floor(this.CANVAS_WIDTH_PX / this.COL_SIZE_PX);

    // How many MS of audio can be shown at once in the canvas
    this.AUDIO_DURATION_IN_DISPLAY_MS =
      ((this.SAMPLES_IN_COLUMN * this.NUM_COLUMNS) / this.SAMPLE_RATE) * 1000;

    this.COL_DURATION_MS = this.AUDIO_DURATION_IN_DISPLAY_MS / this.NUM_COLUMNS;

    // Buffer size inside the Analyzer Node
    this.FFT_SIZE = this.SAMPLES_IN_COLUMN;

    // Set the canvas width
    this.canvas.width = canvasWidthPx;
    this.canvas.height = canvasHeightPx;

    let ctx = this.canvas.getContext("2d");
    if (ctx) {
      this.context = ctx;
    } else {
      throw "Failed to get get 2d context from canvas !";
    }

    if (autoPlay) {
      this.play();
    }
  }

  play = () => {
    if (!this.playing) {
      this.playing = true;
      this.draw();
    }
  };
  pause = () => {
    this.playing = false;
  };
  stop = () => {
    this.playing = false;
    if (this._frame_id) {
      cancelAnimationFrame(this._frame_id);
    }
  };

  addEvents = (newEvents: WaveformEventType[]) => {
    // Add new events but also remove events that are not visible anymore
    const lasTimeInDisplay = Date.now() - this.AUDIO_DURATION_IN_DISPLAY_MS;

    this.events = [
      ...this.events.filter((ev) => ev.endTs > lasTimeInDisplay),
      ...newEvents,
    ];
  };

  addFFTData = (data: { chunk: Float32Array; timestamp: number }) => {
    const { chunk, timestamp } = data;
    // Remove outdated samples
    const lasTimeInDisplay = Date.now() - this.AUDIO_DURATION_IN_DISPLAY_MS;
    this.fftData = this.fftData.filter((s) => s.timestamp > lasTimeInDisplay);

    const samplesSaved = Math.floor(this.FFT_SIZE / this.SAMPLES_IN_COLUMN);
    for (let i = 0; i < samplesSaved; i += 1) {
      const mean =
        [...Array(this.SAMPLES_IN_COLUMN).keys()]
          .map((idx) => idx + i * this.SAMPLES_IN_COLUMN)
          .reduce((acc, idx) => acc + chunk[idx], 0.0) / this.SAMPLES_IN_COLUMN;

      this.fftData.push({
        sample: mean,
        timestamp: timestamp + i * this.COL_DURATION_MS,
      });
    }
  };

  draw = () => {
    if (this.playing) {
      this._frame_id = requestAnimationFrame(this.draw);
    }

    const WIDTH = this.canvas.width;
    const HEIGHT = this.canvas.height;

    this.context.clearRect(0, 0, WIDTH, HEIGHT);

    this.context.lineWidth = 1;
    this.context.strokeStyle = "rgb(0, 0, 0)";
    this.context.fillStyle = "#3891A6";

    // Rightmost of canvas is Date.now()
    // Leftmost is Date.now() - SOUND_BUFFER_DURATION_S * 1000
    // Draw the peaks
    const maxTime = Date.now();
    const minTime = maxTime - this.AUDIO_DURATION_IN_DISPLAY_MS;
    const pxPerMs = WIDTH / this.AUDIO_DURATION_IN_DISPLAY_MS;

    this.fftData
      .filter((ffts) => ffts.timestamp > minTime)
      .forEach((ffts) => {
        const start = Math.max(ffts.timestamp, minTime);
        const s = ffts.sample * 4;

        const isInCough1 = this.events.some(
          (soundEvent) =>
            soundEvent.startTs < ffts.timestamp &&
            soundEvent.endTs > ffts.timestamp &&
            (soundEvent.eventType == "COUGH" ||
              soundEvent.eventType == "COUGH_1")
        );
        const isInCough2 = this.events.some(
          (soundEvent) =>
            soundEvent.startTs < ffts.timestamp &&
            soundEvent.endTs > ffts.timestamp &&
            soundEvent.eventType == "COUGH_2"
        );

        if (isInCough1 && isInCough2) {
          this.context.fillStyle = "#F4C095";
        } else if (isInCough1) {
          this.context.fillStyle = "#EE2E31";
        } else if (isInCough2) {
          this.context.fillStyle = "#c97298";
        } else {
          this.context.fillStyle = "#3891A6";
        }

        roundRect(
          this.context,
          Math.floor((start - minTime) * pxPerMs),
          (HEIGHT / 2) * (1 - s),
          Math.floor(this.COL_SIZE_DATA_PX),
          HEIGHT * s,
          this.COL_SIZE_DATA_PX / 2
        ).fill();
      });

    this.context.fillStyle = "rgb(33, 52, 88,0.2)";
    this.events
      .filter((soundEvent) => soundEvent.endTs > minTime)
      .filter((soundEvent) => soundEvent.eventType === "PEAK")
      .forEach((soundEvent) => {
        const start = Math.max(soundEvent.startTs, minTime);
        this.context.fillRect(
          Math.floor((start - minTime) * pxPerMs),
          Math.floor(HEIGHT * 0.4),
          Math.floor((soundEvent.endTs - start) * pxPerMs),
          Math.floor(HEIGHT * 0.2)
        );
      });

    this.context.fillStyle = "rgba(0, 0, 0,0.1)";
    this.events
      .filter((soundEvent) => soundEvent.endTs > minTime)
      .filter((soundEvent) => soundEvent.eventType === "FRAME")
      .forEach((soundEvent) => {
        const start = Math.max(soundEvent.startTs, minTime);
        this.context.fillRect(
          Math.floor((start - minTime) * pxPerMs),
          Math.floor(HEIGHT * 0.1),
          Math.floor((soundEvent.endTs - start) * pxPerMs),
          Math.floor(HEIGHT * 0.8)
        );
        this.context.strokeRect(
          Math.floor((start - minTime) * pxPerMs),
          Math.floor(HEIGHT * 0.1),
          Math.floor((soundEvent.endTs - start) * pxPerMs),
          Math.floor(HEIGHT * 0.8)
        );
      });
  };
}
